// "TOP 20 najtańszych tras z miasta X" — landing dla `/loty/$city`.
// Wczytujemy real-time z route_prices przez istniejący `getDealsFromOrigin`.

import { useQuery } from "@tanstack/react-query";
import { DealLink, DealDate } from "@/components/deal-link";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { getDealsFromOrigin, type Deal } from "@/lib/deals-from-origin.functions";
import { PriceBadge } from "@/components/price-badge";

export function CityTopDealsCard({
  from,
  fromName,
  limit = 20,
}: {
  from: string;
  fromName?: string;
  limit?: number;
}) {
  const fetchDeals = useServerFn(getDealsFromOrigin);
  const { data, isLoading } = useQuery<Deal[]>({
    queryKey: ["city-top-deals", from, limit],
    queryFn: () => fetchDeals({ data: { from, limit } }),
    staleTime: 15 * 60 * 1000, // 15 min — odzwierciedla świeżość route_prices
  });

  const deals = data ?? [];
  if (!isLoading && deals.length === 0) return null;

  return (
    <section className="glass mt-8 rounded-2xl p-5">
      <header className="mb-4">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold">
          <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
          Najtańsze loty {fromName ? `z ${fromName}` : ""} ({from})
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Aktualne najniższe ceny „od" — wszystkie kierunki z {fromName ?? from}, posortowane od
          najtańszego.
        </p>
      </header>

      {isLoading ? (
        <ul className="grid gap-2 sm:grid-cols-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <li key={i} className="h-14 animate-pulse rounded-lg bg-muted/40" aria-hidden="true" />
          ))}
        </ul>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {deals.map((d, i) => (
            <li key={`${d.from}-${d.to}`}>
              <DealLink
                from={d.from}
                to={d.to}
                departDate={d.departDate}
                className="group flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-card/40 px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-card/80"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 truncate font-medium">
                    <span className="text-xs text-muted-foreground font-mono shrink-0">
                      {String(i + 1).padStart(2, "0")}.
                    </span>
                    <span className="truncate">{d.city}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{d.country}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {d.from} → {d.to}
                    </span>
                    {d.badge && <PriceBadge badge={d.badge} size="xs" />}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="font-mono text-sm font-bold text-primary">{d.price}</span>
                  <DealDate departDate={d.departDate} />
                  <ArrowRight
                    className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                    aria-hidden="true"
                  />
                </div>
              </DealLink>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
